import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getStockReport, getProductStockSummary, exportStockSummaryToExcel, exportStockDetailsToExcel } from "../../components/Api/GmsStockReport";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSuppliers, searchSuppliers } from "../../components/Api/GmsSupplier";
import { Eye, Package, BarChart3, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getUserPermissions,
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const emptyFilter = {
  Business_Id: null,
  Branch_Id: null,
  Supplier_Id: null,
  Product_Code: "",
  Product_Name: "",
  BatchNumber: "",
  TransactionFlag: null,
  ExpiryDateFrom: null,
  ExpiryDateTo: null,
  PageNumber: 1,
  PageSize: 50,
};

export default function StockReportPage() {
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Data states
  const [productSummaryData, setProductSummaryData] = useState([]);
  const [detailedStockData, setDetailedStockData] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Supplier search states
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSearchTimeout, setSupplierSearchTimeout] = useState(null);
  const [selectedSupplierFilterOption, setSelectedSupplierFilterOption] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal states
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockModalLoading, setStockModalLoading] = useState(false);

  // Session management
  const session = useMemo(() => getUserSession(), []);

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadBusinesses();
      loadSuppliers();
    }
  }, [permissionsLoading, canView]);

  // Auto-populate business if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !selectedBusiness) {
      const businessOption = businesses.find(b => b.value === session.businessId);
      if (businessOption) {
        setSelectedBusiness(businessOption);
        setFilter(prev => ({ ...prev, Business_Id: session.businessId }));
      }
    }
  }, [businesses, session.businessId, selectedBusiness]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (
      session.branchId &&
      branches.length > 0 &&
      selectedBusiness?.value === session.businessId &&
      !selectedBranch
    ) {
      const branchOption = branches.find(b => b.value === session.branchId);
      if (branchOption) {
        setSelectedBranch(branchOption);
        setFilter(prev => ({ ...prev, Branch_Id: session.branchId }));
      }
    }
  }, [branches, session.branchId, selectedBusiness, selectedBranch, session.businessId]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness]);

  // Auto-load product summary when business/branch is auto-populated
  useEffect(() => {
    if (!permissionsLoading && canView && session.businessId && businesses.length > 0) {
      loadProductSummary(filter);
    }
  }, [filter.Business_Id, filter.Branch_Id, permissionsLoading, canView]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);

      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsStockReport');
      const hasEdit = hasEditOrHigherPermission(userPermissions, '/GmsStockReport');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsStockReport');

      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);

      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({});
      const businessData = Array.isArray(data) ? data : (data.data || []);
      const businessOptions = businessData.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn || b.business_NameAr || b.business_Name
      }));
      setBusinesses(businessOptions);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async (businessId) => {
    try {
      if (!businessId) {
        setBranches([]);
        return;
      }
      const data = await getBranches({ Branch_Buisness_Id: businessId });
      const branchData = Array.isArray(data) ? data : (data.data || []);
      const branchOptions = branchData.map(b => ({
        value: b.branch_Id || b.Branch_Id,
        label: b.branch_NameEn || b.Branch_NameEn || b.branch_Name || b.Branch_Name
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers({});
      const supplierData = Array.isArray(data) ? data : (data.data || []);
      setSuppliers(supplierData);
    } catch (error) {
      console.error("Error loading suppliers:", error);
    }
  };

  // Manual search function for supplier filter search
  const searchSuppliersManually = async (inputValue) => {
    console.log('🔍 searchSuppliersManually called with inputValue:', inputValue);

    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';

    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setSupplierSearchOptions([]);
      return;
    }

    try {
      setSupplierSearchLoading(true);
      console.log('🚀 Making API call to searchSuppliers with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchSuppliers(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);

      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setSupplierSearchOptions([]);
        return;
      }

      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';

        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) ||
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );

        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }

        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;

        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });

      console.log('📋 Formatted options:', formattedOptions);
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  const loadProductSummary = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getProductStockSummary(filterData);
      console.log('Loaded product summary data:', data);

      if (data.success && data.data) {
        setProductSummaryData(data.data.products || []);
        setTotalCount(data.data.totalRecords || 0);
        setTotalPages(data.data.totalPages || 0);
      } else {
        setProductSummaryData([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error loading product summary:", error);
      Swal.fire("Error", "Failed to load product summary: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadDetailedStock = async (productId) => {
    setStockModalLoading(true);
    try {
      const filterData = {
        ...filter,
        Product_Id: productId,
        PageNumber: 1,
        PageSize: 1000, // Get all stock for this product
      };

      const data = await getStockReport(filterData);
      console.log('Loaded detailed stock data:', data);

      if (data.success && data.data) {
        setDetailedStockData(data.data.stockItems || []);
      } else {
        setDetailedStockData([]);
      }
    } catch (error) {
      console.error("Error loading detailed stock:", error);
      Swal.fire("Error", "Failed to load stock details: " + error.message, "error");
    } finally {
      setStockModalLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const handleBusinessChange = (option) => {
    setSelectedBusiness(option);
    setSelectedBranch(null);
    handleFilterChange("Business_Id", option ? option.value : null);
    handleFilterChange("Branch_Id", null);
  };

  const handleBranchChange = (option) => {
    setSelectedBranch(option);
    handleFilterChange("Branch_Id", option ? option.value : null);
  };

  const handleApplyFilter = () => {
    setPage(1);
    const filterData = {
      ...filter,
      PageNumber: 1,
    };
    loadProductSummary(filterData);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    setSelectedSupplierFilterOption(null);
    setSupplierSearchOptions([]);
    loadProductSummary(emptyFilter);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const filterData = {
        Business_Id: filter.Business_Id,
        Branch_Id: filter.Branch_Id,
        Supplier_Id: filter.Supplier_Id,
        Product_Code: filter.Product_Code || null,
        Product_Name: filter.Product_Name || null,
        PageNumber: 1,
        PageSize: 999999 // Large number to fetch all records
      };

      await exportStockSummaryToExcel(filterData);
      Swal.fire("Success", "Stock summary report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting report:", error);
      Swal.fire("Error", "Failed to export stock summary report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportStockDetails = async (product) => {
    try {
      setLoading(true);
      const filterData = {
        Business_Id: filter.Business_Id,
        Branch_Id: filter.Branch_Id,
        Supplier_Id: filter.Supplier_Id,
        Product_Id: product.product_Id,
        Product_Code: filter.Product_Code || null,
        Product_Name: filter.Product_Name || null,
        BatchNumber: filter.BatchNumber || null,
        TransactionFlag: filter.TransactionFlag,
        ExpiryDateFrom: filter.ExpiryDateFrom,
        ExpiryDateTo: filter.ExpiryDateTo,
        PageNumber: 1,
        PageSize: 999999 // Large number to fetch all records
      };

      await exportStockDetailsToExcel(filterData);
      Swal.fire("Success", `Stock details for ${product.product_NameEn || product.product_Code} exported successfully`, "success");
    } catch (error) {
      console.error("Error exporting stock details:", error);
      Swal.fire("Error", "Failed to export stock details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    const filterData = {
      ...filter,
      PageNumber: newPage,
    };
    loadProductSummary(filterData);
  };

  const handleViewStock = async (product) => {
    setSelectedProduct(product);
    setShowStockModal(true);
    await loadDetailedStock(product.product_Id);
  };

  const handleCloseStockModal = () => {
    setShowStockModal(false);
    setSelectedProduct(null);
    setDetailedStockData([]);
  };

  // Options for dropdowns
  const businessOptions = businesses;
  const branchOptions = branches;

  // Keep supplierOptions for backward compatibility if needed elsewhere
  const supplierOptions = suppliers.map(supplier => ({
    value: supplier.supplier_Id,
    label: supplier.supplier_NameEn || supplier.supplier_Name
  }));

  const transactionFlagOptions = [
    { value: 1, label: "Purchase" },
    { value: 2, label: "Purchase Confirm" },
    { value: 3, label: "Purchase Return" },
    { value: 4, label: "Purchase Return Confirmation" },
    { value: 5, label: "Transfer In" },
    { value: 6, label: "Transfer Confirmation" },
    { value: 7, label: "Sales" },
    { value: 8, label: "Sales Return" },
  ];

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Stock Report" breadcrumbItem="Reports" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">
                    <BarChart3 className="me-2" size={24} />
                    Stock Report
                  </h4>
                  <Button
                    color="success"
                    onClick={handleExportExcel}
                    disabled={loading || productSummaryData.length === 0}
                    className="d-flex align-items-center gap-2"
                  >
                    <Download size={16} />
                    Export Excel
                  </Button>
                </div>

                {/* Filter Section */}
                <Card className="mb-4">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">Filter Products</h5>
                      <Button
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>

                    {showFilters && (
                      <>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Company</Label>
                              <Select
                                isClearable
                                value={selectedBusiness}
                                onChange={handleBusinessChange}
                                options={businessOptions}
                                placeholder="Select Company..."
                                isSearchable
                                styles={selectStyles}
                                isDisabled={!!session.businessId}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Branch</Label>
                              <Select
                                isClearable
                                value={selectedBranch}
                                onChange={handleBranchChange}
                                options={branchOptions}
                                placeholder="Select Branch..."
                                isSearchable
                                styles={selectStyles}
                                isDisabled={!!session.branchId || !selectedBusiness}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Supplier</Label>
                              <Select
                                isClearable
                                value={selectedSupplierFilterOption}
                                onChange={(option) => {
                                  setSelectedSupplierFilterOption(option);
                                  handleFilterChange("Supplier_Id", option ? option.value : null);
                                }}
                                options={supplierSearchOptions}
                                placeholder="Search by name..."
                                isSearchable
                                styles={selectStyles}
                                isLoading={supplierSearchLoading}
                                noOptionsMessage={() => "Type at least 3 characters"}
                                loadingMessage={() => "Searching..."}
                                filterOption={null}
                                onInputChange={(inputValue) => {
                                  console.log('🔤 Filter Input changed:', inputValue);

                                  // Clear previous timeout
                                  if (supplierSearchTimeout) {
                                    clearTimeout(supplierSearchTimeout);
                                  }

                                  // Trim the input value before searching
                                  const trimmedValue = inputValue ? inputValue.trim() : '';

                                  // Set new timeout
                                  const timeout = setTimeout(() => {
                                    if (trimmedValue.length >= 3) {
                                      searchSuppliersManually(trimmedValue);
                                    } else {
                                      setSupplierSearchOptions([]);
                                    }
                                  }, 300);
                                  setSupplierSearchTimeout(timeout);

                                  return inputValue;
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Product Code</Label>
                              <Input
                                type="text"
                                value={filter.Product_Code}
                                onChange={(e) => handleFilterChange("Product_Code", e.target.value)}
                                placeholder="Enter Product Code..."
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Product Name</Label>
                              <Input
                                type="text"
                                value={filter.Product_Name}
                                onChange={(e) => handleFilterChange("Product_Name", e.target.value)}
                                placeholder="Enter Product Name..."
                              />
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply Filter
                          </Button>
                          <Button color="warning" onClick={handleClearFilter}>
                            Clear Filter
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Product Summary Table */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Product Stock Summary ({totalCount} products)
                    </h5>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Product Code</th>
                            <th>Product Name</th>
                            <th>Unit</th>
                            <th>Supplier</th>
                            <th>Available Quantity</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="7" className="text-center">
                                <ClipLoader size={30} color="#556ee6" />
                                <p className="mt-2">Loading product summary...</p>
                              </td>
                            </tr>
                          ) : productSummaryData.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center">
                                No products found
                              </td>
                            </tr>
                          ) : (
                            productSummaryData.map((product, index) => (
                              <tr key={product.product_Id}>
                                <td>{(page - 1) * pageSize + index + 1}</td>
                                <td>{product.product_Code}</td>
                                <td>{product.product_NameEn}</td>
                                <td>{product.unit_NameEn}</td>
                                <td>{product.supplier_NameEn || '-'}</td>
                                <td className="text-center">
                                  <span className={`badge ${product.availableQuantity > 0 ? 'bg-success' : 'bg-danger'}`}>
                                    {product.availableQuantity}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => handleViewStock(product)}
                                      disabled={product.availableQuantity === 0}
                                    >
                                      <Eye size={16} />
                                      View Stock
                                    </Button>
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() => handleExportStockDetails(product)}
                                      disabled={loading || product.availableQuantity === 0}
                                    >
                                      <Download size={16} />
                                      Export
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                        style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                        <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                          <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                          Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>

                          {[...Array(Math.min(5, totalPages))].map((_, index) => {
                            const pageNum = Math.max(1, Math.min(totalPages, page - 2 + index));
                            const isActive = page === pageNum;

                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}

                          <Button
                            color="light"
                            size="sm"
                            disabled={page === totalPages}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === totalPages ? '#f8f9fa' : '#fff',
                              color: page === totalPages ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Detailed Stock Modal */}
        <Modal
          isOpen={showStockModal}
          toggle={handleCloseStockModal}
          size="xl"
          scrollable
        >
          <ModalHeader toggle={handleCloseStockModal}>
            <Package className="me-2" size={20} />
            Stock Details - {selectedProduct?.product_NameEn}
          </ModalHeader>
          <ModalBody>
            {stockModalLoading ? (
              <div className="text-center p-4">
                <ClipLoader size={50} color="#556ee6" />
                <p className="mt-2">Loading stock details...</p>
              </div>
            ) : detailedStockData.length === 0 ? (
              <div className="text-center p-4">
                <p>No stock details available for this product.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table bordered striped>
                  <thead className="table-light">
                    <tr>
                      <th>Sr.No</th>
                      <th>Batch Number</th>
                      <th>Expiry Date</th>
                      <th>Branch</th>
                      <th>Cost Price</th>
                      <th>Sales Price</th>
                      <th>Transaction Type</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailedStockData.map((stockItem, index) => (
                      <tr key={stockItem.purchaseStock_Id}>
                        <td>{index + 1}</td>
                        <td>{stockItem.purchaseStock_BatchNo || 'N/A'}</td>
                        <td>
                          {stockItem.purchaseStock_ExpDate
                            ? stockItem.purchaseStock_ExpDate  // Already in DD/MM/YYYY format from API
                            : '-'
                          }
                        </td>
                        <td>{stockItem.branch_NameEn || 'N/A'}</td>
                        <td className="text-end">{(stockItem.purchaseStock_CostPrice || 0).toFixed(2)}</td>
                        <td className="text-end">{(stockItem.purchaseStock_SalesPrice || 0).toFixed(2)}</td>
                        <td>{stockItem.transactionFlagDescription}</td>
                        <td>
                          {stockItem.purchaseStock_ExpDate && (
                            <span className={`badge ${stockItem.isExpired ? 'bg-danger' : stockItem.isCloseToExpiry ? 'bg-warning' : 'bg-success'}`}>
                              {stockItem.isExpired ? 'Expired' : stockItem.isCloseToExpiry ? 'Close to Expiry' : 'Good'}
                            </span>
                          )}
                          {!stockItem.purchaseStock_ExpDate && (
                            <span className="badge bg-secondary">No Expiry</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCloseStockModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
